import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, setPublicOrigin, type PublicUser } from '../lib/authApi';

export interface AuthState {
  /** `undefined` while the initial status fetch is in flight. */
  user: PublicUser | null | undefined;
  needsBootstrap: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setUser: (user: PublicUser | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Loads `/api/auth/status` once and hands the result to everything below.
 *
 * Wraps the whole app (not just the dashboard) so a board opened by an
 * invited team member can also read the logged-in account for presence,
 * while guests — the common case for a shared board link — simply see
 * `user: null` and nothing here gets in their way.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PublicUser | null | undefined>(undefined);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const status = await authApi.status();
      setPublicOrigin(status.publicUrl);
      setUser(status.user);
      setNeedsBootstrap(status.needsBootstrap);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось проверить сессию');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, needsBootstrap, error, refresh, setUser, logout }),
    [user, needsBootstrap, error, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
